import Button from "components/form/Button";
import { SettingsValue } from "types/payments";
import React, { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  onSetShowDisclosure,
  updateCheckAmountState,
  useIsFinalDisclosureLoading,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
} from "store/slices/paymentsSlice";
import { formatContent, replaceMarkers } from "utils/HelperFunctions";
import { RootState } from "store/store";
import { useStateDisclaimer } from "hooks/useStateDisclaimer";

type DisclosureFinalProps = {
  generalMarkers: { [key: string]: string };
  handleAccept: () => Promise<void>;
  isDrawer?: boolean;
  isOpen?: boolean;
};
const DisclosureFinal = (props: DisclosureFinalProps) => {
  const {
    handleAccept,
    generalMarkers,
    isDrawer = false,
    isOpen = false,
  } = props;
  const portalSettings = usePortalSettingsData();
  const { paymentMarkers, companyMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );

  const isFinalDisclousreLoading = useIsFinalDisclosureLoading();
  const showFinalDisclosure = useShowFinalDisclosure();

  const contentRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const isActive = isOpen || showFinalDisclosure;
  const hasPortalSettings = Object.keys(portalSettings || {}).length > 0;
  const usesStateOverride =
    portalSettings?.payment_agreement_override === SettingsValue.YES;
  const stateDisclaimer = useStateDisclaimer(
    isActive && hasPortalSettings && usesStateOverride
  );
  const stateText = String(stateDisclaimer.data?.final_disclosure ?? "");
  const disclosureSource =
    usesStateOverride && stateText.trim()
      ? stateText
      : (portalSettings?.payment_agreement_text ?? "");
  const isContentLoading =
    !hasPortalSettings ||
    (isActive &&
      usesStateOverride &&
      (stateDisclaimer.status === "idle" ||
        stateDisclaimer.status === "loading"));

  const disclosureText = useMemo(() => {
    try {
      const replacedHtml = replaceMarkers(disclosureSource, {
        ...companyMarkers,
        ...paymentMarkers,
        ...generalMarkers,
      });

      return formatContent(replacedHtml);
    } catch (error) {
      console.log("handleUpdateContent error =====", error);
      return "";
    }
  }, [companyMarkers, disclosureSource, generalMarkers, paymentMarkers]);

  useEffect(() => {
    if (showFinalDisclosure) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      });
    }
  }, [showFinalDisclosure]);

  return (
    <div className="text-base h-full w-full text-start flex flex-col justify-between relative">
      {!isDrawer && (
        <button
          type="button"
          aria-label="Close payment agreement"
          className={`absolute top-0 right-0 ${
            isFinalDisclousreLoading
              ? "cursor-not-allowed"
              : "cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          } z-10`}
          onClick={async () => {
            if (!isFinalDisclousreLoading) {
              dispatch(resetDuplicateOrder());
              dispatch(resetCheckDuplicatePayment());
              dispatch(updateCheckAmountState(false));
              await dispatch(onSetShowDisclosure(false));
            }
          }}
        >
          <CloseIcon />
        </button>
      )}
      <div ref={contentRef} className="max-h-[90%] h-full flex flex-col pt-2">
        <p className="text-txtPrimary text-2xl font-medium h-fit">
          Final Payment Disclosure:
        </p>
        {/* Must be divs, not <p>: the text is authored HTML and routinely
            contains block-level tags. A <p> cannot legally contain them, so the
            parser closed the paragraph early and the content escaped its box.
            List styling is restored here because the global reset strips
            markers, which silently drops the numbering authored in settings. */}
        <div className="h-full pt-5 overflow-y-scroll ">
          <div
            id="final-disclosure-content"
            className="text-txtPrimary break-words
              [&_ol]:list-decimal [&_ul]:list-disc
              [&_ol]:pl-6 [&_ul]:pl-6 [&_li]:pl-1 [&_li]:mb-3
              [&_ol]:mb-3 [&_ul]:mb-3"
            dangerouslySetInnerHTML={{ __html: disclosureText }}
          ></div>
        </div>
      </div>
      <div className="w-full py-3">
        <Button
          text="Accept"
          className="w-full"
          isLoading={isFinalDisclousreLoading}
          onClick={handleAccept}
          variant="variable"
          isDisabled={isContentLoading}
        />
      </div>
    </div>
  );
};

const CloseIcon = () => {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_9442_36466)">
        <path
          d="M3.295 4.22697L13.9016 14.8336M3.295 14.8336L13.9016 4.22697"
          stroke="#8C90A5"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_9442_36466">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export default DisclosureFinal;
